import {useMemo} from 'react'

import {computeReplacementTotals, ReplacementTotals} from './replacementTotals'

import {useRefractoriesQuery} from '@refra/modules/common/hooks/queries'
import {Refractory} from '@refra/modules/refra-catalog/types'
import {StaticMaterialInstallationDTO} from '@refra/modules/refra-static-parts/types'

export * from './replacementTotals'

/**
 * Fetches the refractories referenced by the replacement and returns the computed
 * per-area/grand totals together with a loading flag.
 */
export const useReplacementTotals = (
  materialInstallations: StaticMaterialInstallationDTO[]
): ReplacementTotals & {refractories: Refractory[]; isLoading: boolean} => {
  const assignedRefractoryIds = useMemo(() => {
    const ids = new Set<number>()
    materialInstallations.forEach((installation) =>
      installation.layers?.forEach((layer) => {
        if (layer.refractoryId) ids.add(layer.refractoryId)
      })
    )
    return Array.from(ids)
  }, [materialInstallations])

  const {data: assignedRefractories} = useRefractoriesQuery({
    ids: assignedRefractoryIds,
    enabled: assignedRefractoryIds.length > 0
  })

  const refractories = useMemo(
    () => assignedRefractories?.refractories ?? [],
    [assignedRefractories]
  )

  const totals = useMemo(
    () => computeReplacementTotals(materialInstallations, refractories),
    [materialInstallations, refractories]
  )

  const isLoading = assignedRefractoryIds.length > 0 && !assignedRefractories

  return {...totals, refractories, isLoading}
}
